import { oc } from "@orpc/contract";
import { z } from "zod";
import { ProcSavedReportDocumentSchema } from "@erp/shared-types";

/**
 * Saved query-builder reports.
 *
 * Per-user CRUD over the persisted {@link ProcSavedReportDocumentSchema} blob
 * (query IR + visual report layout), stored in `core_saved_reports`. The `list`
 * response embeds the full document so the "Open" dialog can load a report
 * without a second round-trip.
 */

export const ProcSavedReportSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** The report's owner (always the requesting user — reports are per-user). */
  createdBy: z.object({ id: z.string().uuid(), name: z.string() }),
  report: ProcSavedReportDocumentSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const CreateProcSavedReportInputSchema = z.object({
  name: z.string().min(1).max(255),
  report: ProcSavedReportDocumentSchema,
});

export const UpdateProcSavedReportInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  report: ProcSavedReportDocumentSchema.optional(),
});

export const DeleteProcSavedReportInputSchema = z.object({
  id: z.string().uuid(),
});

export const procSavedReportsContracts = {
  create: oc
    .input(CreateProcSavedReportInputSchema)
    .output(ProcSavedReportSchema)
    .route({ method: "POST" }),
  list: oc
    .output(z.object({ reports: z.array(ProcSavedReportSchema) }))
    .route({ method: "GET" }),
  update: oc
    .input(UpdateProcSavedReportInputSchema)
    .output(ProcSavedReportSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteProcSavedReportInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({ method: "POST" }),
} as const;

export type ProcSavedReport = z.infer<typeof ProcSavedReportSchema>;
export type CreateProcSavedReportInput = z.infer<
  typeof CreateProcSavedReportInputSchema
>;
export type UpdateProcSavedReportInput = z.infer<
  typeof UpdateProcSavedReportInputSchema
>;
export type DeleteProcSavedReportInput = z.infer<
  typeof DeleteProcSavedReportInputSchema
>;
